import * as THREE from 'three';
import { PELL_WOOD_COLOR_URL, PELL_WOOD_NORMAL_URL, PELL_WOOD_REPEAT_Y } from '../config/constants';

/**
 * Shared CC0 wood maps, downloaded once and reused for the app lifetime (the pell
 * is a singleton that's rebuilt in place; target discs spawn continuously). The
 * pell and the disc need different tiling, so each gets its own cached THREE.Texture
 * cloned from the shared image source — one download, no per-instance allocation.
 *
 * None of these are disposed: they live as long as the app and back every pell
 * rebuild / target spawn.
 */

type WoodBase = { colorBase: THREE.Texture; normalBase: THREE.Texture };

let basePromise: Promise<WoodBase> | null = null;

function loadBase(): Promise<WoodBase> {
  basePromise ??= (() => {
    const loader = new THREE.TextureLoader();
    const one = (url: string): Promise<THREE.Texture> =>
      new Promise((resolve, reject) => loader.load(url, resolve, undefined, reject));
    return Promise.all([one(PELL_WOOD_COLOR_URL), one(PELL_WOOD_NORMAL_URL)])
      .then(([colorBase, normalBase]) => {
        colorBase.colorSpace = THREE.SRGBColorSpace;
        return { colorBase, normalBase };
      })
      .catch((err: unknown) => {
        basePromise = null; // allow a later retry
        throw err;
      });
  })();
  return basePromise;
}

export type PellWoodMaps = { color: THREE.Texture; normal: THREE.Texture };

let pellMaps: PellWoodMaps | null = null;

/** Pell maps: color + normal tiled vertically along the post. Cached singleton. */
export async function loadPellWoodMaps(): Promise<PellWoodMaps> {
  if (pellMaps) return pellMaps;
  const { colorBase, normalBase } = await loadBase();
  const color = colorBase.clone();
  const normal = normalBase.clone();
  for (const tex of [color, normal]) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1, PELL_WOOD_REPEAT_Y);
    tex.needsUpdate = true;
  }
  pellMaps = { color, normal };
  return pellMaps;
}

let discColor: THREE.Texture | null = null;

/** Disc map: a single shared color texture (no tiling) reused by every target. */
export async function loadDiscWoodColor(): Promise<THREE.Texture> {
  if (discColor) return discColor;
  const { colorBase } = await loadBase();
  discColor = colorBase;
  return discColor;
}
